import { themeModeAtom } from '@twistopayments/design-system/components/atoms/theme-provider'
import { thmemeChangeAnimation } from '@twistopayments/design-system/components/atoms/theme-provider/theme-change-animation'
import type { ThemeModes } from '@twistopayments/design-system/theme'
import { useAtom } from 'jotai'

import { useMatchMediaQuery } from '@twisto/hooks'

/**
 * Resolves the mode `DesignSystemProvider` renders in.
 *
 * `DesignSystemProvider` takes a fixed `themeMode`, while the appearance
 * setting is a user choice of `light` / `dark` / `auto` persisted in
 * `themeModeAtom`. This reproduces what the design system's own
 * `ThemeProvider` does internally, so the settings screen, the config editor
 * and the system preference all keep driving the theme -- without nesting a
 * second emotion `ThemeProvider` inside the one the design system mounts.
 */
export const useThemeMode = (): ThemeModes => {
  const [themeMode] = useAtom(themeModeAtom)

  const isDarkModePreferred = useMatchMediaQuery(
    '(prefers-color-scheme: dark)',
    thmemeChangeAnimation
  )

  const detectedThemeMode =
    themeMode === 'auto'
      ? isDarkModePreferred
        ? 'dark'
        : 'light'
      : (themeMode as ThemeModes)

  if (typeof window !== 'undefined') {
    window.document.documentElement.setAttribute(
      'data-theme',
      detectedThemeMode
    )
  }

  return detectedThemeMode
}
